import request from '@/modules/base/util/request'
import lang from '@/lang'
import { groupBy, orderBy } from 'lodash'
import BankingListModel from '@/modules/banking/components/list/models/banking-list-model'

import requestConfig from '@/config'
import config from '@/modules/banking/config'
import { fetchLatestTurnoverForBankAccount } from '@/redux/ducks/banking/_helpers/fetchLatestTurnoverForBankAccount'
import { sanitizeListColumns } from '@/redux/util/sanitizeListColumns'
import BankingListColumnModel from '@/modules/banking/components/list/models/banking-list-column-model'
import { fetchBankAccountList } from '@/redux/ducks/banking/bankAccountList'

import {
  updateItems,
  listLoading,
  setListSyncing,
  listError,
  settingsError,
  settingsUpdate,
  updateData,
  LIST_TYPES,
} from '@/redux/ducks/lists'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { typedUpdateColumnSetting } from './_helpers/typedUpdateColumnSetting'

const { BANKING_LIST_TYPE } = LIST_TYPES

export const showLoader = () => listLoading(BANKING_LIST_TYPE)
export const hideLoader = () => listLoading(BANKING_LIST_TYPE, false)
export const setTransaction = (transaction) => updateData(BANKING_LIST_TYPE, { transaction })

export const getLatestTurnoverForBankAccount = (bankAccountId, bankAccountInfo) => {
  return (dispatch) => {
    dispatch(setListSyncing(BANKING_LIST_TYPE, true))

    return fetchLatestTurnoverForBankAccount(bankAccountId, bankAccountInfo)
      .then(() => {
        return dispatch(fetchBankAccountList())
      })
      .then((bankAccounts) => {
        const bankAccount = bankAccounts.find(ba => ba.id === bankAccountId)
        return dispatch(fetchList(bankAccountId, bankAccount.accountType))
      })
      .catch((error) => {
        toast.showToast({ error, bankAccountInfo, message: lang.bankingBankAccountNotFoundErrorMessage })
      })
      .finally(() => dispatch(setListSyncing(BANKING_LIST_TYPE, false)))
  }
}

export const updateColumnSort = (data) => {
  return (dispatch) => {
    const sendRequest = request(`${config.urls.listSetting}/sort`, {
      auth: true,
      method: 'PUT',
      data: data.filter(item => item.name),
    })

    sendRequest
      .catch((error) => {
        dispatch(settingsError(BANKING_LIST_TYPE, error))
      })
  }
}

export const updateColumnSetting = (data, updateFromSettingModal) => {
  return typedUpdateColumnSetting(data, updateFromSettingModal, BANKING_LIST_TYPE, config.urls.listSetting, fetchListSetting)
}

export const fetchListSetting = () => {
  return (dispatch) => {
    request(config.urls.listSetting, { auth: true, isConcurrent: true })
      .then((response) => {
        const {
          body: {
            data: { autoSizeColumns, columns },
            meta: {
              defaultSort,
              sortBy,
            },
          },
        } = response

        const sanitizedColumns = sanitizeListColumns(columns, defaultSort, sortBy)
        const bankingColumns = sanitizedColumns.map((column) => {
          column.displayed = true
          column.hide = !column.active

          return new BankingListColumnModel(column)
        })

        dispatch(settingsUpdate(BANKING_LIST_TYPE, { autoSizeColumns, columns: bankingColumns }))
      })
      .catch((error) => {
        dispatch(settingsError(BANKING_LIST_TYPE, error))
      })
  }
}

export const fetchList = (id, accountType) => {
  return (dispatch) => {
    dispatch(listLoading(BANKING_LIST_TYPE))
    const options = {
      offset: 0,
      limit: 99999, // -1 not possible
      searchTerm: '',
      filter: {
        type: 'all',
      },
    }
    const url = config.urls.fetchTurnoverListForBankAccount(id, options, accountType)
    return request(url, { auth: true, isConcurrent: true })
      .then(response => {
        const {
          body: {
            data: items,
            meta,
          },
        } = response

        const groupItemsByDate = groupBy(items, 'bookingDate')
        const findItemWithMaxOrderId = Object.keys(groupItemsByDate).map(key => orderBy(groupItemsByDate[key], 'orderId', 'desc')[0])

        const turnovers = items.map((item) => {
          const findAccountBalance = findItemWithMaxOrderId.find(accountId => accountId.id === item.id)
          const isAccountBalance = !!findAccountBalance & accountType !== 'all'
          return new BankingListModel({ ...item, isAccountBalance })
        })

        dispatch(updateItems(BANKING_LIST_TYPE, turnovers, meta))
      })
      .catch((error) => {
        dispatch(listError(BANKING_LIST_TYPE, error))
      })
  }
}

export const autoAssign = (account) => {
  const { accountType: type, id } = account
  return (dispatch) => {
    const url = config.urls.autoAssign(id)
    return request(url, { auth: true, isConcurrent: true, timeout: requestConfig.requestExtraLongTimeout })
      .then(response => {
        const state = response?.body?.data?.transactionState
        if (state === 'succeeded') {
          toast.showToast({ message: lang.banking.list.reassignmentDone, bankAccountInfo: account })
          dispatch(fetchList(id, type))
        }
        if (state === 'running' || state === 'queued') {
          toast.showToast({ message: lang.banking.list.reassignmentInProgress, bankAccountInfo: account })
        }
        if (state === 'already_running') {
          toast.showToast({ message: lang.banking.list.reassignmentAlreadyStarted, bankAccountInfo: account })
        }
      })
      .catch((error) => {
        if (error.code !== 'ETIMEDOUT') {
          toast.showToast({ error, bankAccountInfo: account, message: lang.banking.list.reassignmentError })
        } else {
          toast.showToast({ type: 'error', message: lang.banking.list.reassignmentLonger })
        }
      })
  }
}
